/** 비교 — apps on one plane: market (x) × ease of entry (y) (S6, `/compare`).
 *
 *  Ease = 10 − (0.6 × competition + 0.4 × entry risk), the weighting the
 *  overall score uses, so the top-right corner is the best bet. A dot is
 *  filled with its overall-score band and sized by keyword count; the three
 *  best by overall score wear a numbered badge. The table under the plot is
 *  the same data and decides which apps are drawn.
 *
 *  Below 768px there is no plot (it is square and unreadable at 390px): the
 *  table alone, with one line saying where the plot went. */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChartScatter } from 'lucide-react'
import type { AnalysisRow } from '@/api/client'
import { useAnalyses } from '@/lib/queries'
import { scoreBand, statusOf } from '@/lib/status'
import { cn } from '@/lib/utils'
import {
  AppIcon,
  ButtonLink,
  CountryTag,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  initials,
  PageHeader,
  Scatter,
  ScoreNumber,
  Segmented,
  Skeleton,
  StatusPill,
  TonePill,
  fitDomain,
  useIsDesktop,
  useQueryParam,
  type Column,
  type ScatterPoint,
} from '@/ui'

/** 0–10, higher is easier: the same blend the overall score uses. */
export const easeOf = (a: AnalysisRow['analysis']): number => 10 - (a.competition_score * 0.6 + a.entry_risk_score * 0.4)

/** Rank of every app by overall score, best first (1-based). */
export function rankByOverall(rows: AnalysisRow[]): Map<number, number> {
  const sorted = [...rows].sort((a, b) => b.analysis.overall_score - a.analysis.overall_score)
  return new Map(sorted.map((r, i) => [r.app.id, i + 1]))
}

/** The plot's dots for the apps that are ticked. Radius grows with the
 *  square root of the keyword count, 12–24px. */
export function toPoints(rows: AnalysisRow[], allRows: AnalysisRow[], ranks: Map<number, number>): ScatterPoint[] {
  const maxKw = Math.max(1, ...allRows.map((r) => r.keyword_count))
  return rows.map((r) => ({
    id: r.app.id,
    x: r.analysis.market_score,
    y: easeOf(r.analysis),
    r: 12 + Math.sqrt(r.keyword_count / maxKw) * 12,
    tone: statusOf('score', scoreBand(r.analysis.overall_score)).tone,
    text: initials(r.app.name),
    rank: ranks.get(r.app.id),
  }))
}

type Zoom = 'full' | 'fit'
const ZOOMS = [
  { value: 'full' as const, label: '전체 0–10' },
  { value: 'fit' as const, label: '맞춤' },
]

const BAND_KEY: { key: string; range: string }[] = [
  { key: 'good', range: '7 이상' },
  { key: 'fair', range: '5 이상' },
  { key: 'low', range: '3 이상' },
  { key: 'poor', range: '3 미만' },
]

export default function Compare() {
  const [comp, setComp] = useQueryParam('comp')
  const [zoomParam, setZoom] = useQueryParam('zoom', 'full')
  const includeCompetitors = comp === '1'
  const zoom: Zoom = zoomParam === 'fit' ? 'fit' : 'full'
  const desktop = useIsDesktop()

  const q = useAnalyses(includeCompetitors)
  const rows = useMemo(() => q.data?.items ?? [], [q.data])
  const [hidden, setHidden] = useState<Set<number>>(new Set())
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const ranks = useMemo(() => rankByOverall(rows), [rows])
  const sorted = useMemo(() => [...rows].sort((a, b) => (ranks.get(a.app.id) ?? 0) - (ranks.get(b.app.id) ?? 0)), [rows, ranks])
  const visible = useMemo(() => rows.filter((r) => !hidden.has(r.app.id)), [rows, hidden])
  const points = useMemo(() => toPoints(visible, rows, ranks), [visible, rows, ranks])
  const domain = useMemo(() => (zoom === 'fit' ? fitDomain(points) : undefined), [zoom, points])
  const byId = useMemo(() => new Map(rows.map((r) => [r.app.id, r])), [rows])

  const toggle = (id: number) =>
    setHidden((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // The quadrant words only mean something when the 5/5 cross is on the plot.
  const crossInside = !domain || (domain.xMin <= 5 && domain.xMax >= 5 && domain.yMin <= 5 && domain.yMax >= 5)

  const columns: Column<AnalysisRow>[] = [
    {
      key: 'show',
      header: <span className="sr-only">차트에 표시</span>,
      width: '44px',
      card: 'hidden',
      cell: (r) => (
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={!hidden.has(r.app.id)}
            onChange={() => toggle(r.app.id)}
            aria-label={`${r.app.name} 차트에 표시`}
            className="h-4 w-4 cursor-pointer accent-accent"
          />
        </span>
      ),
    },
    {
      key: 'app',
      header: '앱',
      card: 'title',
      cell: (r) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <span className="w-5 shrink-0 text-right text-meta tabular-nums text-muted-foreground">{ranks.get(r.app.id)}</span>
          <AppIcon src={r.app.icon_url} name={r.app.name} size={28} />
          <Link
            to={`/apps/${r.app.id}/keywords`}
            onClick={(e) => e.stopPropagation()}
            className="min-w-0 truncate font-medium hover:text-accent hover:underline"
          >
            {r.app.name}
          </Link>
          {!r.app.is_mine && <StatusPill domain="app" status="competitor" />}
        </span>
      ),
    },
    {
      key: 'overall',
      header: '종합',
      width: '72px',
      align: 'right',
      card: 'line',
      cell: (r) => <ScoreNumber value={r.analysis.overall_score} />,
    },
    { key: 'market', header: '시장', width: '132px', card: 'line', cell: (r) => <ScoreBar label="시장" value={r.analysis.market_score} fill="bg-chart-1" /> },
    { key: 'comp', header: '경쟁', width: '132px', card: 'line', cell: (r) => <ScoreBar label="경쟁" value={r.analysis.competition_score} fill="bg-chart-2" /> },
    { key: 'risk', header: '위험', width: '132px', card: 'line', cell: (r) => <ScoreBar label="진입 위험" value={r.analysis.entry_risk_score} fill="bg-chart-5" /> },
    {
      key: 'kw',
      header: '키워드',
      width: '72px',
      align: 'right',
      className: 'tabular-nums text-muted-foreground',
      card: 'line',
      cell: (r) => r.keyword_count.toLocaleString('ko-KR'),
    },
  ]

  return (
    <>
      <PageHeader title="비교" description="앱을 시장 점수(가로)와 진입 쉬움(세로)에 놓는다. 오른쪽 위가 가장 좋은 자리다.">
        <FilterBar
          end={
            <span className="text-table tabular-nums text-muted-foreground">
              {visible.length} / {rows.length}
            </span>
          }
        >
          <label className="inline-flex cursor-pointer select-none items-center gap-2 whitespace-nowrap text-table">
            <input
              type="checkbox"
              checked={includeCompetitors}
              onChange={(e) => setComp(e.target.checked ? '1' : '')}
              className="h-4 w-4 cursor-pointer accent-accent"
            />
            경쟁사 포함
          </label>
          {desktop && <Segmented label="축 범위" size="sm" value={zoom} onChange={(v) => setZoom(v === 'full' ? '' : v)} options={ZOOMS} />}
        </FilterBar>
      </PageHeader>

      {q.error ? (
        <ErrorState title="점수를 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
      ) : q.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ChartScatter}
          title="아직 점수가 있는 앱이 없다"
          description="앱의 점수 탭에서 점수 계산을 누르면 여기에 놓인다."
          actions={
            <ButtonLink to="/apps" variant="secondary">
              앱 목록
            </ButtonLink>
          }
        />
      ) : (
        <div className="space-y-4">
          {desktop ? (
            <section className="grid gap-4 rounded-lg border border-border bg-card p-4 lg:grid-cols-[minmax(0,640px)_minmax(0,1fr)]" aria-label="산점도">
              <Scatter
                label="앱 비교 산점도: 시장 점수와 진입 쉬움"
                points={points}
                domain={domain}
                xLabel="시장 점수 →"
                yLabel="진입 쉬움 →"
                quadrants={crossInside ? ['틈새', '최우선', '피함', '치열'] : undefined}
                selectedId={selectedId}
                onSelect={setSelectedId}
                tooltip={(p) => {
                  const r = byId.get(p.id)
                  return r ? <HoverCard row={r} rank={ranks.get(r.app.id) ?? 0} /> : null
                }}
              />
              <Key />
            </section>
          ) : (
            <p className="text-table text-muted-foreground">산점도는 넓은 화면에서 보인다.</p>
          )}
          <DataTable
            label="앱 점수 비교"
            rows={sorted}
            rowKey={(r) => r.app.id}
            columns={columns}
            onRowClick={(r) => setSelectedId((cur) => (cur === r.app.id ? null : r.app.id))}
            rowClassName={(r) => cn(r.app.id === selectedId && 'bg-accent/10')}
          />
        </div>
      )}
    </>
  )
}

/** A score on a 0–10 bar with its number beside it. The number is always
 *  there, so a pale bar (chart slot 3 is low contrast on white) loses
 *  nothing. */
function ScoreBar({ label, value, fill }: { label: string; value: number; fill: string }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap tabular-nums" title={`${label} ${value.toFixed(1)}`}>
      <span role="img" aria-label={`${label} ${value.toFixed(1)} / 10`} className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-muted">
        <span className={cn('block h-full rounded-full', fill)} style={{ width: `${Math.max(0, Math.min(100, value * 10))}%` }} />
      </span>
      <span className="w-7 text-right">{value.toFixed(1)}</span>
    </span>
  )
}

/** How to read the plot, beside it. */
function Key() {
  return (
    <div className="space-y-4 text-table lg:pt-2">
      <div>
        <h2 className="text-section font-semibold">읽는 법</h2>
        <ul className="mt-2 space-y-1.5 text-muted-foreground">
          <li>가로는 시장 점수, 세로는 진입 쉬움이다. 쉬움은 경쟁과 진입 위험이 낮을수록 높다.</li>
          <li>점 크기는 키워드 수다. 점을 누르면 가로·세로 선이 그어진다.</li>
          <li>숫자 배지는 종합 점수 1~3위다.</li>
        </ul>
      </div>
      <div>
        <h3 className="text-meta font-medium text-muted-foreground">점 색 (종합 점수)</h3>
        <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1.5">
          {BAND_KEY.map((b) => {
            const def = statusOf('score', b.key)
            return (
              <li key={b.key} className="inline-flex items-center gap-1.5">
                <TonePill tone={def.tone} label={def.label} />
                <span className="text-meta text-muted-foreground">{b.range}</span>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

function HoverCard({ row, rank }: { row: AnalysisRow; rank: number }) {
  const a = row.analysis
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <AppIcon src={row.app.icon_url} name={row.app.name} size={32} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-body font-semibold">{row.app.name}</div>
          <div className="flex items-center gap-1.5 text-micro text-muted-foreground">
            <CountryTag code={row.app.country} /> 종합 {rank}위
          </div>
        </div>
        <ScoreNumber value={a.overall_score} size="lg" />
      </div>
      <dl className="grid grid-cols-3 gap-1.5 text-center">
        {[
          ['시장', a.market_score],
          ['경쟁', a.competition_score],
          ['위험', a.entry_risk_score],
        ].map(([label, v]) => (
          <div key={label as string} className="rounded bg-muted py-1">
            <dt className="text-micro text-muted-foreground">{label}</dt>
            <dd className="font-semibold tabular-nums">{(v as number).toFixed(1)}</dd>
          </div>
        ))}
      </dl>
      <div className="text-micro text-muted-foreground">추적 키워드 {row.keyword_count}개</div>
    </div>
  )
}
